import { ApiConfig } from "../apiConfig/apiConfig";
import { ApiCallGet, ApiCallPost, ApiCallDelete } from "../apiConfig/apiCall";
import AuthService from "./AuthService";
import { mapFiatError } from "./fiatErrors";
import { asMoneyString, newIdempotencyKey, toAedGrossString } from "./fiatMoney";

function fiatError(res) {
  return mapFiatError(res);
}

function listQuery(params = {}) {
  const qs = new URLSearchParams();
  if (params.page != null) qs.set("page", String(params.page));
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.status) qs.set("status", String(params.status));
  if (params.side) qs.set("side", String(params.side));
  const s = qs.toString();
  return s ? `?${s}` : "";
}

/** `GET /v1/fiat/virtual-accounts/me` — null when no VA (`VA_NOT_FOUND`). */
export async function fetchVirtualAccountMe() {
  const { baseFiatApi, fiatVirtualAccountMe } = ApiConfig;
  const url = baseFiatApi + fiatVirtualAccountMe;
  const res = await ApiCallGet(url);
  if (res?.success && res.data) {
    return { ok: true, account: res.data, error: null };
  }
  if (res?.code === "VA_NOT_FOUND") {
    return { ok: false, account: null, error: null, notFound: true };
  }
  return { ok: false, account: null, error: fiatError(res), notFound: false };
}

/** `POST /v1/fiat/virtual-accounts` — KYC required; idempotent when VA exists. */
export async function createVirtualAccount() {
  const { baseFiatApi, fiatVirtualAccounts } = ApiConfig;
  const url = baseFiatApi + fiatVirtualAccounts;
  const idempotencyKey = newIdempotencyKey("va");
  const res = await ApiCallPost(url, {}, { "Idempotency-Key": idempotencyKey });
  if (res?.success && res.data) {
    return { ok: true, account: res.data, created: res.message?.includes("created"), error: null };
  }
  return { ok: false, account: null, created: false, error: fiatError(res) };
}

/** `GET /v1/fiat/limits` */
export async function fetchFiatLimits() {
  const { baseFiatApi, fiatLimits } = ApiConfig;
  const url = baseFiatApi + fiatLimits;
  const res = await ApiCallGet(url);
  if (res?.success) {
    return { ok: true, limits: res.data || null, error: null };
  }
  return { ok: false, limits: null, error: fiatError(res) };
}

/** Spot wallet row from `GET /v1/wallet/user-wallet?wallet_type=spot`. */
export async function fetchSpotBalance(code) {
  const want = String(code || "").toUpperCase();
  try {
    const res = await AuthService.getUserfunds("spot");
    if (!res?.success || !Array.isArray(res.data)) {
      return { ok: false, balance: "0", locked: "0", row: null };
    }
    const row =
      res.data.find((r) => String(r?.short_name || "").toUpperCase() === want) ||
      res.data.find((r) => String(r?.currency || "").toUpperCase() === want) ||
      null;
    if (!row) {
      return { ok: true, balance: "0", locked: "0", row: null };
    }
    return {
      ok: true,
      balance: String(row.balance ?? "0"),
      locked: String(row.locked_balance ?? "0"),
      row,
    };
  } catch {
    return { ok: false, balance: "0", locked: "0", row: null };
  }
}

/** Spot wallet AED row from `GET /v1/wallet/user-wallet?wallet_type=spot`. */
export async function fetchSpotAedBalance() {
  return fetchSpotBalance("AED");
}

export function formatAedAmount(value) {
  const n = Number.parseFloat(String(value ?? "0"));
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function numOrNull(value) {
  if (value == null || value === "") return null;
  if (typeof value === "object") return null;
  const n = Number.parseFloat(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function firstNum(...vals) {
  for (const v of vals) {
    const n = numOrNull(v);
    if (n != null) return n;
  }
  return null;
}

function firstStr(...vals) {
  for (const v of vals) {
    if (v == null || v === "") continue;
    if (typeof v === "object") continue;
    return String(v);
  }
  return null;
}

function unwrapLimitsRoot(limits) {
  if (!limits || typeof limits !== "object") return {};
  if (limits.withdraw || limits.withdrawal || limits.deposit) return limits;
  if (limits.limits && typeof limits.limits === "object") return unwrapLimitsRoot(limits.limits);
  if (limits.data && typeof limits.data === "object" && !Array.isArray(limits.data)) {
    return unwrapLimitsRoot(limits.data);
  }
  return limits;
}

function periodFrom(bucket, period) {
  const nested = bucket?.[period] && typeof bucket[period] === "object" ? bucket[period] : null;
  const used = firstNum(
    nested?.used,
    nested?.used_amount,
    nested?.consumed,
    bucket?.[`${period}_used`],
    bucket?.[`${period}Used`],
    bucket?.[`used_${period}`]
  );
  const limit = firstNum(
    nested?.limit,
    nested?.cap,
    nested?.max,
    nested?.amount,
    bucket?.[`${period}_limit`],
    bucket?.[`${period}_max`],
    bucket?.[`max_${period}`],
    bucket?.[period] != null && typeof bucket[period] !== "object" ? bucket[period] : null
  );
  const remaining = firstNum(
    nested?.remaining,
    nested?.left,
    nested?.available,
    bucket?.[`${period}_remaining`],
    bucket?.[`${period}Remaining`]
  );
  return {
    used: used != null ? used : limit != null && remaining != null ? Math.max(0, limit - remaining) : null,
    limit,
    remaining: remaining != null ? remaining : limit != null && used != null ? Math.max(0, limit - used) : null,
  };
}

function parseLimitSide(bucket, root, prefix) {
  const src = bucket && typeof bucket === "object" ? bucket : {};
  const daily = periodFrom(src, "daily");
  const monthly = periodFrom(src, "monthly");
  if (daily.limit == null && daily.used == null) {
    const fromRoot = periodFrom(root, prefix === "withdraw" ? "withdraw_daily" : "deposit_daily");
    if (fromRoot.limit != null || fromRoot.used != null) {
      daily.used = fromRoot.used;
      daily.limit = fromRoot.limit;
      daily.remaining = fromRoot.remaining;
    }
  }
  return {
    dailyLimit: daily.limit,
    dailyRemaining: daily.remaining,
    dailyUsed: daily.used,
    monthlyLimit: monthly.limit,
    monthlyRemaining: monthly.remaining,
    monthlyUsed: monthly.used,
    minPerTx: firstNum(
      src.min_per_tx,
      src.min,
      src.min_amount,
      prefix === "deposit" ? src.min_deposit : src.min_withdrawal
    ),
    maxPerTx: firstNum(
      src.max_per_tx,
      src.max,
      src.max_amount,
      prefix === "deposit" ? src.max_deposit : src.max_withdrawal
    ),
  };
}

export function parseFiatWithdrawLimits(limits) {
  const root = unwrapLimitsRoot(limits);
  const bucket = root.withdraw || root.withdrawal;
  if (!bucket || typeof bucket !== "object") {
    return {
      dailyLimit: null,
      dailyRemaining: null,
      dailyUsed: null,
      monthlyLimit: null,
      monthlyRemaining: null,
      monthlyUsed: null,
      minPerTx: null,
      maxPerTx: null,
    };
  }
  return parseLimitSide(bucket, root, "withdraw");
}

export function parseFiatDepositLimits(limits) {
  const root = unwrapLimitsRoot(limits);
  const bucket = root.deposit && typeof root.deposit === "object" ? root.deposit : {};
  const parsed = parseLimitSide(bucket, root, "deposit");
  const empty =
    parsed.dailyLimit == null &&
    parsed.monthlyLimit == null &&
    parsed.dailyUsed == null &&
    parsed.dailyRemaining == null &&
    parsed.monthlyRemaining == null &&
    parsed.minPerTx == null &&
    parsed.maxPerTx == null;
  const base = empty ? parseLimitSide(root, root, "deposit") : parsed;
  const src = empty ? root : bucket;
  return {
    ...base,
    depositsToday: firstNum(src.deposits_today, src.depositsToday),
    freeDepositsRemaining: firstNum(src.free_deposits_remaining, src.freeDepositsRemaining),
    nextDepositFeeAed: firstStr(src.next_deposit_fee_aed, src.nextDepositFeeAed),
    depositFeeTz: firstStr(src.deposit_fee_tz, src.depositFeeTz) || "Asia/Dubai",
  };
}

export function parseFiatConvertLimits(limits) {
  const root = unwrapLimitsRoot(limits);
  const src = root.convert && typeof root.convert === "object" ? root.convert : {};
  return {
    minAed: firstNum(src.min_aed, src.min, src.min_amount, src.min_per_tx),
    maxAed: firstNum(src.max_aed_per_tx, src.max_aed, src.max_per_tx, src.max, src.max_amount),
  };
}

/** `GET /v1/fiat/withdrawals/preview` — fee quote, no lock. */
export async function previewFiatWithdrawal({ amount, whitelist_id, beneficiary_id }) {
  const { baseFiatApi, fiatWithdrawalsPreview } = ApiConfig;
  const qs = new URLSearchParams();
  qs.set("amount", toAedGrossString(amount) || asMoneyString(amount));
  const whitelistId = whitelist_id || beneficiary_id;
  if (whitelistId) qs.set("whitelist_id", String(whitelistId));
  const url = `${baseFiatApi}${fiatWithdrawalsPreview}?${qs.toString()}`;
  const res = await ApiCallGet(url);
  if (res?.success && res.data) {
    return { ok: true, preview: res.data, error: null };
  }
  return { ok: false, preview: null, error: fiatError(res) };
}

/** `POST /v1/fiat/withdrawals` — AED withdrawal to a verified whitelist bank account. */
export async function submitFiatWithdrawal(body) {
  const { baseFiatApi, fiatWithdrawals } = ApiConfig;
  const url = baseFiatApi + fiatWithdrawals;
  const payload = {
    whitelist_id: body.whitelist_id || body.beneficiary_id,
    amount: toAedGrossString(body.amount) || asMoneyString(body.amount),
    currency: "AED",
  };
  if (body.email_code) payload.email_code = String(body.email_code).trim();
  if (body.mobile_code) payload.mobile_code = String(body.mobile_code).trim();
  if (body.google_authenticator_code) {
    payload.google_authenticator_code = String(body.google_authenticator_code).trim();
  }
  if (body.fund_password) payload.fund_password = String(body.fund_password).trim();
  const idempotencyKey = body.idempotency_key || newIdempotencyKey("wd");
  const res = await ApiCallPost(url, payload, { "Idempotency-Key": idempotencyKey });
  if (res?.success) {
    return { ok: true, data: res.data || null, message: res.message || "", error: null };
  }
  return { ok: false, data: null, message: null, error: fiatError(res) };
}

function listedWhitelistEntries(items) {
  return (items || []).filter((b) => b && b.status && b.status !== "DISABLED");
}

/** `GET /v1/fiat/whitelist` */
export async function fetchBeneficiaries(status) {
  const { baseFiatApi, fiatWhitelist } = ApiConfig;
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  const url = baseFiatApi + fiatWhitelist + qs;
  const res = await ApiCallGet(url);
  if (res?.success && res.data) {
    return { ok: true, items: listedWhitelistEntries(res.data.items), error: null };
  }
  return { ok: false, items: [], error: fiatError(res) };
}

/** `POST /v1/fiat/whitelist` */
export async function createBeneficiary(body) {
  const { baseFiatApi, fiatWhitelist } = ApiConfig;
  const url = baseFiatApi + fiatWhitelist;
  const res = await ApiCallPost(url, body);
  if (res?.success && res.data) {
    return {
      ok: true,
      beneficiary: res.data,
      otpSentTo: res.data.otp_sent_to || "email",
      error: null,
    };
  }
  return { ok: false, beneficiary: null, otpSentTo: null, error: fiatError(res) };
}

/** `POST /v1/fiat/whitelist/:id/verify` */
export async function verifyBeneficiary(id, otp) {
  const { baseFiatApi, fiatWhitelist } = ApiConfig;
  const url = `${baseFiatApi}${fiatWhitelist}/${encodeURIComponent(String(id))}/verify`;
  const res = await ApiCallPost(url, { otp: String(otp || "").trim() });
  if (res?.success && res.data) {
    return { ok: true, beneficiary: res.data, error: null };
  }
  return { ok: false, beneficiary: null, error: fiatError(res) };
}

/** `POST /v1/fiat/whitelist/:id/resend-otp` */
export async function resendBeneficiaryOtp(id) {
  const { baseFiatApi, fiatWhitelist } = ApiConfig;
  const url = `${baseFiatApi}${fiatWhitelist}/${encodeURIComponent(String(id))}/resend-otp`;
  const res = await ApiCallPost(url, {});
  if (res?.success) {
    return { ok: true, otpSentTo: res.data?.otp_sent_to || "email", error: null };
  }
  return { ok: false, otpSentTo: null, error: fiatError(res) };
}

/** `DELETE /v1/fiat/whitelist/:id` */
export async function removeBeneficiary(id) {
  const { baseFiatApi, fiatWhitelist } = ApiConfig;
  const url = `${baseFiatApi}${fiatWhitelist}/${encodeURIComponent(String(id))}`;
  const res = await ApiCallDelete(url);
  if (res?.success) {
    return { ok: true, error: null };
  }
  return { ok: false, error: fiatError(res) };
}

/** `GET /v1/fiat/deposits` — owner history (API hides UNMATCHED). */
export async function fetchFiatDeposits(params = {}) {
  const { baseFiatApi, fiatDeposits } = ApiConfig;
  const url = `${baseFiatApi}${fiatDeposits}${listQuery(params)}`;
  const res = await ApiCallGet(url);
  if (res?.success) {
    const data = res.data || {};
    return { ok: true, items: data.items || [], meta: data, error: null };
  }
  return { ok: false, items: [], meta: null, error: fiatError(res) };
}

/** `GET /v1/fiat/deposits/:id` */
export async function fetchFiatDeposit(id) {
  const { baseFiatApi, fiatDeposits } = ApiConfig;
  const url = `${baseFiatApi}${fiatDeposits}/${encodeURIComponent(String(id))}`;
  const res = await ApiCallGet(url);
  if (res?.success && res.data) {
    return { ok: true, deposit: res.data, error: null };
  }
  return { ok: false, deposit: null, error: fiatError(res) };
}

/** `POST /v1/fiat/deposit-intents` — declare amount; no wallet move. */
export async function createDepositIntent(body, idempotencyKey) {
  const { baseFiatApi, fiatDepositIntents } = ApiConfig;
  const url = baseFiatApi + fiatDepositIntents;
  const key = idempotencyKey || newIdempotencyKey("di");
  const payload = {
    amount: asMoneyString(body.amount),
    currency: String(body.currency || "AED").toUpperCase(),
  };
  const res = await ApiCallPost(url, payload, { "Idempotency-Key": key });
  if (res?.success && res.data) {
    return { ok: true, intent: res.data, error: null };
  }
  return { ok: false, intent: null, error: fiatError(res) };
}

/** `GET /v1/fiat/deposit-intents` */
export async function fetchDepositIntents(params = {}) {
  const { baseFiatApi, fiatDepositIntents } = ApiConfig;
  const url = `${baseFiatApi}${fiatDepositIntents}${listQuery(params)}`;
  const res = await ApiCallGet(url);
  if (res?.success) {
    const data = res.data || {};
    return { ok: true, items: data.items || [], meta: data, error: null };
  }
  return { ok: false, items: [], meta: null, error: fiatError(res) };
}

/** `GET /v1/fiat/convert/assets` — enabled catalog only. */
export async function fetchConvertAssets() {
  const { baseFiatApi, fiatConvertAssets } = ApiConfig;
  const url = baseFiatApi + fiatConvertAssets;
  const res = await ApiCallGet(url);
  if (res?.success && res.data) {
    return { ok: true, assets: res.data, error: null };
  }
  return { ok: false, assets: null, error: fiatError(res) };
}

/** `GET /v1/fiat/convert/rates` — cached mids + fee schedules (U11 bootstrap / WS fallback). */
export async function fetchConvertRates() {
  const { baseFiatApi, fiatConvertRates } = ApiConfig;
  const url = baseFiatApi + fiatConvertRates;
  const res = await ApiCallGet(url);
  if (res?.success && res.data) {
    return { ok: true, rates: res.data, error: null };
  }
  return { ok: false, rates: null, error: fiatError(res) };
}

/** `POST /v1/fiat/convert/quotes` — no wallet move. */
export async function createConvertQuote(body) {
  const { baseFiatApi, fiatConvertQuotes } = ApiConfig;
  const url = baseFiatApi + fiatConvertQuotes;
  const payload = {
    side: body.side,
    base_asset: body.base_asset,
    quote_asset: body.quote_asset,
  };
  if (body.amount_aed != null) payload.amount_aed = asMoneyString(body.amount_aed);
  if (body.amount_crypto != null) payload.amount_crypto = asMoneyString(body.amount_crypto);
  const res = await ApiCallPost(url, payload);
  if (res?.success && res.data) {
    return { ok: true, quote: res.data, error: null };
  }
  return { ok: false, quote: null, error: fiatError(res) };
}

/** `GET /v1/fiat/convert/quotes/:id` */
export async function fetchConvertQuote(id) {
  const { baseFiatApi, fiatConvertQuotes } = ApiConfig;
  const url = `${baseFiatApi}${fiatConvertQuotes}/${encodeURIComponent(String(id))}`;
  const res = await ApiCallGet(url);
  if (res?.success && res.data) {
    return { ok: true, quote: res.data, error: null };
  }
  return { ok: false, quote: null, error: fiatError(res) };
}

/** `POST /v1/fiat/convert/trades` — execute a live quote. */
export async function executeConvertTrade(quoteId, idempotencyKey) {
  const { baseFiatApi, fiatConvertTrades } = ApiConfig;
  const url = baseFiatApi + fiatConvertTrades;
  const key = idempotencyKey || newIdempotencyKey("cv");
  const res = await ApiCallPost(url, { quote_id: String(quoteId) }, { "Idempotency-Key": key });
  if (res?.success && res.data) {
    return { ok: true, trade: res.data, error: null };
  }
  return { ok: false, trade: null, error: fiatError(res) };
}

/** `GET /v1/fiat/convert/trades` */
export async function fetchConvertTrades(params = {}) {
  const { baseFiatApi, fiatConvertTrades } = ApiConfig;
  const url = `${baseFiatApi}${fiatConvertTrades}${listQuery(params)}`;
  const res = await ApiCallGet(url);
  if (res?.success) {
    const data = res.data || {};
    return { ok: true, items: data.items || [], meta: data, error: null };
  }
  return { ok: false, items: [], meta: null, error: fiatError(res) };
}

/** `GET /v1/fiat/convert/trades/:id` */
export async function fetchConvertTrade(id) {
  const { baseFiatApi, fiatConvertTrades } = ApiConfig;
  const url = `${baseFiatApi}${fiatConvertTrades}/${encodeURIComponent(String(id))}`;
  const res = await ApiCallGet(url);
  if (res?.success && res.data) {
    return { ok: true, trade: res.data, error: null };
  }
  return { ok: false, trade: null, error: fiatError(res) };
}

/** `GET /v1/fiat/withdrawals` */
export async function fetchFiatWithdrawals(params = {}) {
  const { baseFiatApi, fiatWithdrawals } = ApiConfig;
  const url = `${baseFiatApi}${fiatWithdrawals}${listQuery(params)}`;
  const res = await ApiCallGet(url);
  if (res?.success) {
    const data = res.data || {};
    return { ok: true, items: data.items || [], meta: data, error: null };
  }
  return { ok: false, items: [], meta: null, error: fiatError(res) };
}

/** `GET /v1/fiat/withdrawals/:id` */
export async function fetchFiatWithdrawal(id) {
  const { baseFiatApi, fiatWithdrawals } = ApiConfig;
  const url = `${baseFiatApi}${fiatWithdrawals}/${encodeURIComponent(String(id))}`;
  const res = await ApiCallGet(url);
  if (res?.success && res.data) {
    return { ok: true, withdrawal: res.data, error: null };
  }
  return { ok: false, withdrawal: null, error: fiatError(res) };
}

/** `POST /v1/fiat/withdrawals/:id/cancel` — only while awaiting admin. */
export async function cancelFiatWithdrawal(id) {
  const { baseFiatApi, fiatWithdrawals } = ApiConfig;
  const url = `${baseFiatApi}${fiatWithdrawals}/${encodeURIComponent(String(id))}/cancel`;
  const res = await ApiCallPost(url, {});
  if (res?.success) {
    return { ok: true, withdrawal: res.data || null, error: null };
  }
  return { ok: false, withdrawal: null, error: fiatError(res) };
}

export { mapFiatError } from "./fiatErrors";
export { asMoneyString, isPositiveMoneyString, newIdempotencyKey } from "./fiatMoney";

